import { SetStateAction, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton, Popover, TextareaAutosize, Tooltip } from '@mui/material';
import styled from 'styled-components';

import LinkIconSrc from '@/assets/icons/link-2.svg';
import type { HyperlinkFormHandle } from '@/features/canvas/components/HyperlinkForm';
import { HyperlinkForm } from '@/features/canvas/components/HyperlinkForm';
import { ListStyledImage } from '@/features/canvas/components/InitiativesAccordionContent/Fields/InitiativeCustom/InitiativeCustom';
import { CHARACTER_LIMITS } from '@/features/canvas/constants/character-limits';
import { FieldType } from '@/features/canvas/types/field-type';
import { CharacterCounter } from '@/features/ui/components/CharacterCounter';
import { SmallActionButton } from '@/features/ui/components/SmallActionButton/SmallActionButton';
import { components } from '@/types/api';

const InitiativeDescription = ({
  field,
  onUpdateField,
  onClick,
  readOnly,
}: {
  field: components['schemas']['InitiativeField'];
  onClick: (e: React.MouseEvent<HTMLTextAreaElement>) => void;
  onUpdateField: (field: components['schemas']['InitiativeField']) => void;
  readOnly: boolean;
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState<string>(field.value || '');
  const [hyperlinks, setHyperlinks] = useState(field.hyperlinks || []);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const hyperlinkFormRef = useRef<HyperlinkFormHandle | null>(null);

  const [selectionRange, setSelectionRange] = useState<{ end: number; start: number } | null>(null);

  const handleOnSubmit = () => {
    const updatedField = {
      ...field,
      value: inputValue,
      hyperlinks,
    };

    onUpdateField(updatedField);
  };

  const handleInputValueChange = (event: { target: { value: SetStateAction<string> } }) => {
    const newValue = event.target.value;
    if (typeof newValue === 'string' && newValue.length <= CHARACTER_LIMITS.LARGE_FIELD) {
      setInputValue(newValue);
    }
  };

  const open = Boolean(anchorEl);
  const id = open ? 'hyperlink-popover' : undefined;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (hyperlinks.length > 0) {
      hyperlinkFormRef.current?.cancelNextCleanup();
    }

    if (selectionRange) {
      const selectedText = inputValue.slice(selectionRange.start, selectionRange.end);

      setHyperlinks(prev => {
        if (prev.length === 0) return prev;

        const updated = [...prev];
        updated[0] = {
          ...updated[0],
          display_text: selectedText,
          position_start: selectionRange.start,
          position_end: selectionRange.end,
        };
        return updated;
      });
      hyperlinkFormRef.current?.markDirty();
    }

    setAnchorEl(event.currentTarget);
  };

  const handleClose = async () => {
    await hyperlinkFormRef.current?.flushPending?.();
    setAnchorEl(null);
  };

  const handleTextSelection = (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
    const target = e.target as HTMLTextAreaElement;
    const start = target.selectionStart;
    const end = target.selectionEnd;

    if (start === end) {
      setSelectionRange(null);
    } else {
      setSelectionRange({ start, end });
    }
  };

  return (
    <>
      <DescriptionArea
        readOnly={readOnly}
        onClick={e => {
          console.log('🟢 InitiativeDescription DescriptionArea clicked, readOnly:', readOnly);
          onClick(e);
        }}
        value={inputValue}
        minRows={1}
        maxRows={6}
        maxLength={CHARACTER_LIMITS.LARGE_FIELD}
        placeholder={t('editor.sidebar.initiative.description_placeholder')}
        onChange={handleInputValueChange}
        onMouseUp={handleTextSelection}
      />
      <CharacterCounter
        currentLength={inputValue.length}
        maxLength={CHARACTER_LIMITS.LARGE_FIELD}
      />
      {!readOnly && (
        <>
          <Popover
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            PaperProps={{
              sx: theme => ({
                mt: '-85px',
                p: 2,
                maxWidth: 235,
                position: 'relative',
                overflow: 'visible',
                padding: 0,
                borderRadius: 2,
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -16,
                  left: 32,
                  width: 0,
                  height: 0,
                  borderLeft: '8px solid transparent',
                  borderRight: '8px solid transparent',
                  borderTop: `16px solid ${theme.palette.background.paper}`,
                  zIndex: 1,
                },
              }),
            }}
          >
            <PopoverContent>
              <HyperlinkForm
                ref={hyperlinkFormRef}
                hyperlinks={hyperlinks}
                setHyperlinks={setHyperlinks}
                fieldId={field.id!}
                fieldType={FieldType.InitiativeField}
                initiativeFieldType={field.type}
                selectionRange={selectionRange}
                inputValue={inputValue}
              />
            </PopoverContent>
          </Popover>

          <ActionWrapper>
            <Tooltip title={t('Hyperlink')} placement="top">
              <StyledIconButton onClick={handleClick} size="small">
                <ListStyledImage src={LinkIconSrc} />
              </StyledIconButton>
            </Tooltip>
            <SmallActionButton
              text={t('editor.sidebar.initiative.save_button_title')}
              onClick={handleOnSubmit}
            />
          </ActionWrapper>
        </>
      )}
    </>
  );
};

export { InitiativeDescription };

const DescriptionArea = styled(TextareaAutosize)`
  width: 100%;
  border: 1px solid ${({ theme }) => theme.palette.brand.backgroundDialog};
  border-radius: 0.4rem;
  resize: vertical;
  max-height: 120px;
  overflow-y: auto;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.palette.brand.backgroundDialog};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.palette.brand.textSecondary};
    border-radius: 3px;

    &:hover {
      background: ${({ theme }) => theme.palette.brand.textPrimary};
    }
  }

  &:focus-visible {
    border: 1px solid ${({ theme }) => theme.palette.brand.textSecondary};
    outline: none;
  }
`;

const ActionWrapper = styled.div`
  margin-top: 1rem;
  display: flex;
  -webkit-box-pack: end;
  justify-content: flex-end;
  gap: 0.5rem;
`;

const PopoverContent = styled.div`
  padding: 1rem;
  max-width: 400px;
`;

const StyledIconButton = styled(IconButton)`
  padding: 5px;
  width: auto;
  height: 25px;
`;
